import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Newspaper, Tag, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Press = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/press`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryBadge = (category) => {
    const styles = {
      news: { label: 'News', className: 'bg-blue-100 text-blue-700' },
      press: { label: 'Pressemitteilung', className: 'bg-green-100 text-green-700' },
      announcement: { label: 'Ankündigung', className: 'bg-purple-100 text-purple-700' },
    };
    const style = styles[category] || styles.news;
    return <Badge className={style.className}>{style.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-AT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(a => a.category === selectedCategory);

  // Get excerpt from content
  const getExcerpt = (content, maxLength = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-[hsl(215,15%,45%)]">Lade Artikel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-[hsl(210,40%,98%)] pattern-bg relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[hsl(213,80%,35%,0.1)] text-[hsl(213,80%,35%)] text-sm font-medium mb-6">
              <Newspaper className="w-4 h-4 mr-2" />
              Aktuelles & Pressemitteilungen
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[hsl(215,25%,15%)] mb-6">
              Presse & News
            </h1>
            <p className="text-xl text-[hsl(215,15%,45%)] leading-relaxed">
              Neuigkeiten, Pressemitteilungen und wichtige Informationen rund um 
              Väternotruf.com und das Thema Väterrechte in Österreich.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="mb-12">
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <TabsList className="bg-[hsl(210,40%,98%)] border border-[hsl(210,25%,90%)]">
                <TabsTrigger value="all" className="data-[state=active]:bg-[hsl(213,80%,35%)] data-[state=active]:text-white">
                  Alle
                </TabsTrigger>
                <TabsTrigger value="news" className="data-[state=active]:bg-[hsl(213,80%,35%)] data-[state=active]:text-white">
                  News
                </TabsTrigger>
                <TabsTrigger value="press" className="data-[state=active]:bg-[hsl(213,80%,35%)] data-[state=active]:text-white">
                  Presse
                </TabsTrigger>
                <TabsTrigger value="announcement" className="data-[state=active]:bg-[hsl(213,80%,35%)] data-[state=active]:text-white">
                  Ankündigungen
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Articles Grid */}
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <Newspaper className="w-16 h-16 text-[hsl(210,25%,85%)] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[hsl(215,25%,30%)] mb-2">
                Keine Artikel vorhanden
              </h3>
              <p className="text-[hsl(215,15%,45%)]">
                In dieser Kategorie gibt es noch keine Artikel.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Card 
                  key={article.id} 
                  className="card-hover border-[hsl(210,25%,90%)] overflow-hidden group"
                >
                  {article.image_url && (
                    <div className="aspect-video overflow-hidden bg-[hsl(210,40%,96%)]">
                      <img 
                        src={article.image_url} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      {getCategoryBadge(article.category)}
                      <span className="text-sm text-[hsl(215,15%,55%)] flex items-center">
                        <Calendar className="w-3.5 h-3.5 mr-1" />
                        {formatDate(article.created_at)}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-[hsl(215,25%,15%)] mb-2 group-hover:text-[hsl(213,80%,35%)] transition-colors">
                      {article.title}
                    </h3>
                    {article.subtitle && (
                      <p className="text-[hsl(215,15%,45%)] text-sm mb-3">
                        {article.subtitle}
                      </p>
                    )}
                    <p className="text-[hsl(215,15%,45%)] text-sm leading-relaxed mb-4">
                      {getExcerpt(article.content)}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-[hsl(210,25%,92%)]">
                      <span className="text-sm text-[hsl(215,15%,55%)] flex items-center">
                        <User className="w-3.5 h-3.5 mr-1" />
                        {article.author}
                      </span>
                      <Link 
                        to={`/presse/${article.id}`}
                        className="text-[hsl(213,80%,35%)] text-sm font-medium flex items-center hover:underline"
                      >
                        Weiterlesen
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[hsl(210,40%,98%)] border-t border-[hsl(210,25%,92%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-4">
            Presseanfragen?
          </h2>
          <p className="text-[hsl(215,15%,45%)] mb-6">
            Für Presseanfragen und Interviews stehen wir Ihnen gerne zur Verfügung.
          </p>
          <Button asChild className="bg-[hsl(213,80%,35%)] hover:bg-[hsl(213,80%,30%)] text-white">
            <Link to="/kontakt">
              Kontakt aufnehmen
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Press;
